import { IOnInit, IPromise } from "angular";
import { ProcessApiResponse } from "../../../model/ProcessApi";
import { ProcessInformation } from "../../../model/ProcessGroups";
import { TimelineOptions } from "vis-timeline";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../../records/server/serverUtility";
import { ProcessNoteTimelineSource } from "./detail/notes/source";
import {
  TimelineApi,
  TimelineDataItem,
  TimelineIntervalSelectedArgs,
} from "../../../../ui/vis-timeline/api";
import {
  ProcessStateIntervalItemSource,
  ProcessTimelineArgs,
  ProcessTimelineGroup,
} from "./detail/stateInterval/source";
import { ITimelineItemSource } from "./sources";

interface IProcessTimelineItemSource extends ITimelineItemSource<
  ProcessTimelineGroup,
  ProcessTimelineArgs
> {}

export class ProcessTimelineComponentController implements IOnInit {
  /* bindings */
  process!: ProcessApiResponse<any>;
  processInformation!: ProcessInformation;
  /* end bindings */

  selectedInterval: TimelineDataItem | undefined;
  private timelineApi!: TimelineApi;

  private readonly itemSources: Record<string, IProcessTimelineItemSource> = {
    stateInterval: new ProcessStateIntervalItemSource(this.serverUtility),
    notes: new ProcessNoteTimelineSource(this.serverUtility),
  };

  static $inject = [ServerUtilityName];
  constructor(private readonly serverUtility: ServerUtility) {}

  detailComponentContext!: {
    process: ProcessApiResponse<unknown>;
    processInformation: ProcessInformation;
  };
  $onInit(): void {
    this.detailComponentContext = {
      process: this.process,
      processInformation: this.processInformation,
    };
  }

  private initializeItems(
    name: string,
    itemSource: IProcessTimelineItemSource,
    groups: ProcessTimelineGroup[],
  ): IPromise<void> {
    return itemSource
      .getSourceItems({
        name,
        process: this.process,
        processInformation: this.processInformation,
        groups,
      })
      .then((items) => {
        this.timelineApi?.addItems(items);
      });
  }

  intervalSelected(args: TimelineIntervalSelectedArgs<TimelineDataItem>) {
    this.selectedInterval = args.selectedItem;
  }

  timelineApiReady(api: TimelineApi) {
    this.timelineApi = api;
    const timelineGroups = this.getTimelineGroups();
    api.addGroups(timelineGroups);

    const initializePromises = Object.entries(this.itemSources).map(
      ([name, itemSource]) =>
        this.initializeItems(name, itemSource, timelineGroups),
    );

    Promise.all(initializePromises).then(() => {
      this.timelineApi.setOptions(
        this.getTimelineOptions(this.timelineApi.items),
      );
    });
  }

  private getTimelineOptions(
    dataItems: TimelineDataItem<unknown>[],
  ): TimelineOptions {
    const defaultEnd = new Date();
    const defaultStart = new Date(defaultEnd);
    defaultStart.setDate(defaultStart.getDate() - 1);
    const minTime = new Date(
      dataItems.reduce(
        (result, current) => Math.min(result, +current.start),
        Infinity,
      ),
    );
    const maxTime = new Date(
      dataItems.reduce(
        (result, current) =>
          Math.max(result, current.end ? +current.end : result),
        -Infinity,
      ),
    );
    return {
      start: Number.isNaN(minTime.getTime()) ? defaultStart : minTime,
      end: Number.isNaN(maxTime.getTime()) ? defaultEnd : maxTime,
    };
  }

  private getTimelineGroups(): ProcessTimelineGroup[] {
    const groups = this.processInformation.States.map<ProcessTimelineGroup>(
      (s) => ({
        id: s.Id,
        content: s.Name,
        treeLevel: s.Id.split(".").length - 1,
        state: s,
      }),
    );

    for (const group of groups) {
      const parent = groups.find(
        (g) => g.state.Id === group.state.ParentStateId,
      );
      if (!parent) continue;

      (parent.nestedGroups ??= []).push(group.id);
      parent.showNested = false;
    }

    return groups;
  }
}
